import { useMemo, useState } from "react";
import {
  Alert,
  Col,
  DatePicker,
  Row,
  Segmented,
  Statistic,
  Tabs,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { PRICE_ZONES, type PriceZoneKey } from "./biddingZones";
import { ImbalanceForecastMetrics } from "./ImbalanceForecastMetrics";
import { ImbalancePrices } from "./ImbalancePrices";
import { PriceChart } from "./PriceChart";
import { PriceTable } from "./PriceTable";
import { useDayAheadPrices, type PricePoint } from "./useDayAheadPrices";
import { DATE_FORMAT } from "../../../util/formatDate";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const summarise = (points: PricePoint[]) => {
  const values = points.map((point) => point.value);
  return {
    min: Math.min(...values),
    max: Math.max(...values),
    average: values.reduce((sum, value) => sum + value, 0) / values.length,
  };
};

const DayAheadPrices = () => {
  const [zone, setZone] = useState<PriceZoneKey>("BE");
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(1, "day"),
    dayjs().add(1, "day"),
  ]);

  const { actual, forecast, failedDays, isLoading, error } = useDayAheadPrices(
    PRICE_ZONES[zone].biddingZone,
    range[0],
    range[1],
  );

  // Forecast and actual overlap wherever both exist, so summarising them
  // together would count the same period twice.
  const summary = useMemo(() => {
    if (actual.length > 0) {
      return { label: "Actual", ...summarise(actual) };
    }
    if (forecast.length > 0) {
      return { label: "Forecast", ...summarise(forecast) };
    }
    return null;
  }, [actual, forecast]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <Segmented<PriceZoneKey>
            value={zone}
            onChange={setZone}
            options={(Object.keys(PRICE_ZONES) as PriceZoneKey[]).map(
              (key) => ({ label: PRICE_ZONES[key].label, value: key }),
            )}
          />
          <RangePicker
            format={DATE_FORMAT}
            value={range}
            allowClear={false}
            onChange={(value) => {
              if (value?.[0] && value?.[1]) {
                setRange([value[0], value[1]]);
              }
            }}
          />
        </div>
        <Text type="secondary">
          Day-ahead prices published by ENTSO-E, against the prices our
          forecaster predicted for the same periods.
        </Text>
      </div>

      {failedDays > 0 && (actual.length > 0 || forecast.length > 0) && (
        <Alert
          type="warning"
          showIcon
          message={`Prices for ${failedDays} day${failedDays > 1 ? "s" : ""} in this range could not be loaded. Showing the rest.`}
        />
      )}

      {summary && (
        <Row gutter={32} align="middle">
          <Col>
            <Text type="secondary">{summary.label}</Text>
          </Col>
          <Col>
            <Statistic
              title="Min"
              value={summary.min}
              precision={2}
              suffix="€/MWh"
            />
          </Col>
          <Col>
            <Statistic
              title="Average"
              value={summary.average}
              precision={2}
              suffix="€/MWh"
            />
          </Col>
          <Col>
            <Statistic
              title="Max"
              value={summary.max}
              precision={2}
              suffix="€/MWh"
            />
          </Col>
        </Row>
      )}

      <PriceChart
        title={`${PRICE_ZONES[zone].label} day-ahead prices`}
        yAxisTitle="€/MWh"
        actual={actual}
        forecast={forecast}
        isLoading={isLoading}
        error={error}
      />

      <PriceTable actual={actual} forecast={forecast} isLoading={isLoading} />
    </div>
  );
};

export const PricesBoard = () => (
  <Tabs
    defaultActiveKey="day-ahead"
    items={[
      { key: "day-ahead", label: "Day-ahead", children: <DayAheadPrices /> },
      { key: "imbalance", label: "Imbalance", children: <ImbalancePrices /> },
      {
        key: "forecast-value",
        label: "Forecast value",
        children: <ImbalanceForecastMetrics />,
      },
    ]}
  />
);
